import axios from 'axios';
import { API_URL } from '../config/api';

const membershipService = {
  // Get all membership plans
  getPlans: async () => {
    try {
      const response = await axios.get(`${API_URL}/membership/plans`);
      return response.data;
    } catch (error) {
      console.error('Error fetching membership plans:', error);
      throw error;
    }
  },

  // Get single plan
  getPlan: async (id) => {
    try {
      const response = await axios.get(`${API_URL}/membership/plans/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching membership plan:', error);
      throw error;
    }
  },

  // Get all verification badges
  getBadges: async () => {
    try {
      const response = await axios.get(`${API_URL}/membership/badges`);
      return response.data;
    } catch (error) {
      console.error('Error fetching verification badges:', error);
      throw error;
    }
  },

  // Get user's current subscription
  getUserSubscription: async (userId) => {
    try {
      const response = await axios.get(`${API_URL}/membership/subscription/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching user subscription:', error);
      throw error;
    }
  },

  // Get user's badges
  getUserBadges: async (userId) => {
    try {
      const response = await axios.get(`${API_URL}/membership/user-badges/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching user badges:', error);
      throw error;
    }
  },

  // Subscribe to a plan
  subscribe: async (userId, planId, billingPeriod) => {
    try {
      const response = await axios.post(`${API_URL}/membership/subscribe`, {
        userId,
        planId,
        billingPeriod
      });
      return response.data;
    } catch (error) {
      console.error('Error subscribing:', error);
      throw error;
    }
  },

  // Cancel subscription
  cancelSubscription: async (userId, subscriptionId) => {
    try {
      const response = await axios.post(`${API_URL}/membership/cancel-subscription`, {
        userId,
        subscriptionId
      });
      return response.data;
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      throw error;
    }
  },

  // Purchase a badge
  purchaseBadge: async (userId, badgeId) => {
    try {
      const response = await axios.post(`${API_URL}/membership/purchase-badge`, {
        userId,
        badgeId
      });
      return response.data;
    } catch (error) {
      console.error('Error purchasing badge:', error);
      throw error;
    }
  },

  // Submit a verification request with document uploads
  submitVerificationRequest: async (formData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/membership/verification-request`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error submitting verification request:', error);
      throw error;
    }
  },

  // Get verification requests for a user
  getVerificationRequests: async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/membership/verification-requests/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching verification requests:', error);
      throw error;
    }
  },

  // Admin: Get all verification requests
  getAdminVerificationRequests: async (filters = {}) => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.badgeType) params.append('badgeType', filters.badgeType);
      if (filters.search) params.append('search', filters.search);
      
      const response = await axios.get(`${API_URL}/membership/admin/verification-requests?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching admin verification requests:', error);
      throw error;
    }
  },

  // Admin: Approve a verification request
  approveVerificationRequest: async (requestId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(`${API_URL}/membership/admin/verification-requests/${requestId}/approve`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Error approving verification request:', error);
      throw error;
    }
  },

  // Admin: Reject a verification request
  rejectVerificationRequest: async (requestId, adminNotes) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(`${API_URL}/membership/admin/verification-requests/${requestId}/reject`, 
        { adminNotes },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      console.error('Error rejecting verification request:', error);
      throw error;
    }
  },

  // Admin: Mark verification request as under review
  markVerificationUnderReview: async (requestId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(`${API_URL}/membership/admin/verification-requests/${requestId}/review`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Error updating verification request:', error);
      throw error;
    }
  },

  updateSellerVerification: async (sellerId, verificationLevel) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(`${API_URL}/membership/admin/seller-verification/${sellerId}`, 
        { verificationLevel },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating seller verification:', error);
      throw error;
    }
  },

  // Seed default data
  seedData: async () => {
    try {
      const response = await axios.post(`${API_URL}/membership/seed`);
      return response.data;
    } catch (error) {
      console.error('Error seeding membership data:', error);
      throw error;
    }
  },

  // Get user's plan features dynamically from database
  getUserPlanFeatures: async (userId) => {
    try {
      const response = await axios.get(`${API_URL}/membership/user-features/${userId}`, {
        headers: { 'Cache-Control': 'no-cache' }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching user plan features:', error);
      throw error;
    }
  },

  // Get seller's plan features dynamically from database
  getSellerPlanFeatures: async (sellerId) => {
    try {
      const response = await axios.get(`${API_URL}/membership/seller-features/${sellerId}`, {
        headers: { 'Cache-Control': 'no-cache' }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching seller plan features:', error);
      throw error;
    }
  },

  // Get the latest plan update timestamp for cache invalidation
  // This allows frontend to detect when admin has modified plans
  getPlansLastUpdated: async () => {
    try {
      const response = await axios.get(`${API_URL}/membership/plans-last-updated`, {
        headers: { 'Cache-Control': 'no-cache' }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching plans last updated:', error);
      throw error;
    }
  }
};

export default membershipService;
